/**
 * SessionManager - Gestión avanzada de sesión con soporte multi-pestaña
 * 
 * Características:
 * - Logout automático después de 45 minutos de inactividad
 * - Sincronización de actividad entre múltiples pestañas
 * - Logout al cerrar la última pestaña
 * - Validación de sesión expirada al reabrir
 */

const INACTIVITY_TIMEOUT = 45 * 60 * 1000; // 45 minutos en milisegundos
const STORAGE_KEY = 'lastActivityTimestamp';
const CHANNEL_NAME = 'tiresoft-session-sync';
const THROTTLE_DELAY = 1000; // 1 segundo entre actualizaciones de actividad

class SessionManager {
    constructor(logoutCallback) {
        this.logoutCallback = logoutCallback;
        this.inactivityTimer = null;
        this.broadcastChannel = null;
        this.activityEvents = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
        this.lastActivityBroadcast = 0;
        this.tabId = this.generateTabId();
        this.activeTabs = new Set([this.tabId]);

        this.init();
    }

    /**
     * Genera un ID único para esta pestaña
     */
    generateTabId() {
        return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Inicializa el gestor de sesión
     */
    init() {
        console.log('[SessionManager] Initializing session manager for tab:', this.tabId);

        // Verificar si la sesión ha expirado
        if (!this.isSessionValid()) {
            console.log('[SessionManager] Session expired, logging out');
            this.logoutCallback();
            return;
        }

        // Configurar Broadcast Channel para sincronización multi-pestaña
        this.setupBroadcastChannel();

        // Registrar listeners de actividad
        this.registerActivityListeners();

        // Iniciar el timer de inactividad
        this.startInactivityTimer();

        // Registrar evento beforeunload para detectar cierre de pestaña
        window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));

        // Actualizar timestamp inicial
        this.updateLastActivity();

        // Anunciar que esta pestaña está activa
        this.broadcastMessage({ type: 'tab-opened', tabId: this.tabId });
    }

    /**
     * Verifica si la sesión actual es válida
     * @returns {boolean}
     */
    isSessionValid() {
        const lastActivity = localStorage.getItem(STORAGE_KEY);

        if (!lastActivity) {
            return true; // Primera vez, sesión válida
        }

        const now = Date.now();
        const timeSinceLastActivity = now - parseInt(lastActivity, 10);

        return timeSinceLastActivity < INACTIVITY_TIMEOUT;
    }

    /**
     * Configura el Broadcast Channel para comunicación entre pestañas
     */
    setupBroadcastChannel() {
        // Verificar soporte de Broadcast Channel API
        if (typeof BroadcastChannel !== 'undefined') {
            this.broadcastChannel = new BroadcastChannel(CHANNEL_NAME);

            this.broadcastChannel.onmessage = (event) => {
                this.handleBroadcastMessage(event.data);
            };

            console.log('[SessionManager] Broadcast Channel initialized');
        } else {
            console.warn('[SessionManager] Broadcast Channel API not supported');
            // Fallback: usar localStorage events para navegadores antiguos
            this.setupLocalStorageFallback();
        }
    }

    /**
     * Fallback para navegadores que no soportan Broadcast Channel
     */
    setupLocalStorageFallback() {
        window.addEventListener('storage', (event) => {
            if (event.key === 'session-broadcast') {
                try {
                    const data = JSON.parse(event.newValue);
                    this.handleBroadcastMessage(data);
                } catch (e) {
                    console.error('[SessionManager] Error parsing storage event:', e);
                }
            }
        });
    }

    /**
     * Maneja mensajes recibidos de otras pestañas
     */
    handleBroadcastMessage(data) {
        const { type, tabId, timestamp } = data;

        switch (type) {
            case 'activity':
                // Otra pestaña reportó actividad, reiniciar timer
                console.log('[SessionManager] Activity detected in another tab:', tabId);
                this.resetInactivityTimer();
                this.updateLastActivity(timestamp);
                break;

            case 'tab-opened':
                // Nueva pestaña abierta
                console.log('[SessionManager] New tab opened:', tabId);
                this.activeTabs.add(tabId);
                // Responder con nuestra presencia
                this.broadcastMessage({ type: 'tab-alive', tabId: this.tabId });
                break;

            case 'tab-alive':
                // Otra pestaña confirma que está activa
                this.activeTabs.add(tabId);
                break;

            case 'tab-closed':
                // Una pestaña se cerró
                console.log('[SessionManager] Tab closed:', tabId);
                this.activeTabs.delete(tabId);
                break;

            case 'logout':
                // Otra pestaña ejecutó logout
                console.log('[SessionManager] Logout triggered by another tab');
                this.logoutCallback();
                break;
        }
    }

    /**
     * Envía un mensaje a todas las demás pestañas
     */
    broadcastMessage(data) {
        const message = {
            ...data,
            timestamp: Date.now()
        };

        if (this.broadcastChannel) {
            this.broadcastChannel.postMessage(message);
        } else {
            // Fallback usando localStorage
            try {
                localStorage.setItem('session-broadcast', JSON.stringify(message));
                // Limpiar inmediatamente para que el evento se dispare en la próxima vez
                setTimeout(() => localStorage.removeItem('session-broadcast'), 100);
            } catch (e) {
                console.error('[SessionManager] Error broadcasting via localStorage:', e);
            }
        }
    }

    /**
     * Registra listeners para detectar actividad del usuario
     */
    registerActivityListeners() {
        this.boundHandleActivity = this.handleActivity.bind(this);

        this.activityEvents.forEach(event => {
            window.addEventListener(event, this.boundHandleActivity, { passive: true });
        });

        console.log('[SessionManager] Activity listeners registered');
    }

    /**
     * Maneja eventos de actividad del usuario
     */
    handleActivity() {
        const now = Date.now();

        // Throttle: solo procesar actividad una vez por segundo
        if (now - this.lastActivityBroadcast < THROTTLE_DELAY) {
            return;
        }

        this.lastActivityBroadcast = now;
        this.resetInactivityTimer();
        this.updateLastActivity();

        // Notificar a otras pestañas
        this.broadcastMessage({ type: 'activity', tabId: this.tabId });
    }

    /**
     * Actualiza el timestamp de última actividad
     */
    updateLastActivity(timestamp = null) {
        const time = timestamp || Date.now();
        localStorage.setItem(STORAGE_KEY, time.toString());
    }

    /**
     * Inicia el temporizador de inactividad
     */
    startInactivityTimer() {
        this.resetInactivityTimer();
        console.log('[SessionManager] Inactivity timer started (45 minutes)');
    }

    /**
     * Reinicia el temporizador de inactividad
     */
    resetInactivityTimer() {
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
        }

        this.inactivityTimer = setTimeout(() => {
            console.log('[SessionManager] Inactivity timeout reached, logging out');
            this.executeLogout();
        }, INACTIVITY_TIMEOUT);
    }

    /**
     * Maneja el evento beforeunload (cuando se cierra la pestaña)
     */
    handleBeforeUnload(event) {
        console.log('[SessionManager] Tab closing, active tabs:', this.activeTabs.size);

        // Notificar a otras pestañas que esta pestaña se está cerrando
        this.broadcastMessage({ type: 'tab-closed', tabId: this.tabId });

        // Eliminar esta pestaña del conjunto
        this.activeTabs.delete(this.tabId);

        // Si esta es la última pestaña, ejecutar logout
        if (this.activeTabs.size === 0) {
            console.log('[SessionManager] Last tab closing, executing logout');

            // Usar sendBeacon para asegurar que la petición se envíe incluso si la página se cierra
            const logoutUrl = `${axios.defaults.baseURL}/logout`;
            const token = localStorage.getItem('token');

            if (navigator.sendBeacon && token) {
                const blob = new Blob(
                    [JSON.stringify({})],
                    { type: 'application/json' }
                );
                navigator.sendBeacon(logoutUrl, blob);
            }

            // Limpiar datos locales
            this.clearSessionData();
        }
    }

    /**
     * Ejecuta el logout (por inactividad)
     */
    executeLogout() {
        console.log('[SessionManager] Executing logout due to inactivity');

        // Obtener el timestamp de la última actividad
        const lastActivity = localStorage.getItem(STORAGE_KEY);

        // Marcar que el logout es por inactividad y guardar la última actividad
        try {
            sessionStorage.setItem('logout_reason', 'inactivity');
            if (lastActivity) {
                sessionStorage.setItem('last_activity', lastActivity);
            }
        } catch (e) {
            console.error('[SessionManager] Error setting logout reason:', e);
        }

        // Notificar a otras pestañas
        this.broadcastMessage({ type: 'logout', tabId: this.tabId });

        // Ejecutar el callback de logout
        this.logoutCallback();
    }

    /**
     * Limpia los datos de sesión
     */
    clearSessionData() {
        localStorage.removeItem(STORAGE_KEY);
    }

    /**
     * Destruye el gestor de sesión y limpia recursos
     */
    destroy() {
        console.log('[SessionManager] Destroying session manager');

        // Limpiar timer
        if (this.inactivityTimer) {
            clearTimeout(this.inactivityTimer);
        }

        // Remover listeners de actividad
        if (this.boundHandleActivity) {
            this.activityEvents.forEach(event => {
                window.removeEventListener(event, this.boundHandleActivity);
            });
        }

        // Cerrar Broadcast Channel
        if (this.broadcastChannel) {
            this.broadcastChannel.close();
        }

        // Remover listener de beforeunload
        window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
    }
}

// Importar axios para usar en handleBeforeUnload
import { axios2 as axios } from '@/interceptor/useApi';

export default SessionManager;
